import React, { useContext, useState, useEffect, useRef } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import { Environment } from 'src/Environment';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { DateTime } from 'luxon';
import * as Sentry from '@sentry/react';
import Slider from 'src/components/slider/slider';
import {
  businessCashAdvanceDashboardRoute,
  businessCashAdvanceDigitalTermsRoute,
  businessCashAdvanceRoute,
  businessCashAdvanceTopUpContractRoute,
  paymentsDashboardRoute,
} from 'src/routing/routes';
import {
  submitBCAOffer,
  acceptBCAOffer,
  getBCAOffers,
  getSettlementAmount,
} from 'src/services/bca-offers.service';
import { Offer, OfferContext } from 'src/context/offer-context';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import useDebounce from 'src/hooks/use-debounce';
import AcceptContractModal from './accept-contract-modal/accept-contract-modal';
import AdditionalFieldsModal, {
  AdditionalFieldsValues,
} from './additional-fields-modal/additional-fields-modal';
import TealiumEvents from 'src/helpers/tealium-events';
import { getContractPdfBase64 } from 'src/helpers/contract';
import { FullPageLoader, StaticLoader, Tooltip } from 'src/components';
import HeaderNav from '../../header-nav/header-nav';
import ApprovedModal from './approved-modal/approved-modal';
import { IMerchantDetails } from 'src/common/types';
import toastMessageServiceSingleton from 'src/services/toast-message.service';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { SubmitModal } from './submit-modal/submit-modal';
import { Delay, controlledTimeout } from 'src/helpers/delay';
import { InfoImage } from 'src/assets/images';
import { useIsMobile } from 'src/features/common/hooks/use-is-mobile';
import NavigationFooter from 'src/containers/self-service/navigation-footer/navigation-footer';
import {
  BCALinkID,
  BCAPageName,
  BCAPageUrl,
  BCATopUpTealiumService,
} from 'src/containers/self-service/business-cash-advance/tagging/bca-top-up-tealium-service';
import { SENTRY_TAG_KEYS } from 'src/types';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

export enum BCASubmitStatus {
  NONE = 'NONE',
  COLLECTING_INFO = 'COLLECTING_INFO',
  PREPARING_APPLICATION = 'PREPARING_APPLICATION',
  SUBMITTING_APPLICATION = 'SUBMITTING_APPLICATION',
  SUCCESS = 'SUCCESS',
  TAKING_LONG = 'TAKING_LONG',
}

export default function BusinessCashAdvanceTopUpCalculation() {
  const history = useHistory();
  const isMobile = useIsMobile();
  const [hideNextButton, setHideNextButton] = useState(false);
  const { merchant, setMerchantDetails } = useMerchantProfile();
  const {
    offers,
    selectedOffer,
    additionalFieldValues,
    setAdditionalFieldValues,
    setChosenOffer,
    setSelectedOffer,
    earlySettlementAmount,
    setEarlySettlementAmount,
    setIsBCATopUp,
  } = useContext(OfferContext);
  const [redirect, setRedirect] = useState<string>();
  const [isLoading, setIsLoading] = React.useState(false);
  const [amountOptions, setAmountOptions] = useState<string[] | []>([]);
  const [fundedAmountIndex, setFundedAmountIndex] = useState<number>(0);
  const [paymentPeriodIndex, setPaymentPeriodIndex] = useState<number>(0);
  const [periodOptions, setPeriodOptions] = useState<number[]>([]);
  const [currentOffer, setCurrentOffer] = useState<Offer>();
  const [offerLoading, setOfferLoading] = useState<boolean>(false);
  const [bcaSubmitStatus, setBcaSubmitStatus] = useState<BCASubmitStatus>(BCASubmitStatus.NONE);

  const debouncedFundedIndex = useDebounce(fundedAmountIndex, 1000);
  const debouncedPaymentPeriod = useDebounce(paymentPeriodIndex, 1000);
  const [offerAcceptLoading, setOfferAcceptLoading] = useState<boolean>(false);

  const [showAdditionalFieldsModal, setShowAdditionalFieldsModal] = useState<boolean>(false);
  const [showContractModal, setShowContractModal] = useState<boolean>(false);
  const [showApprovedModal, setShowApprovedModal] = useState<boolean>(false);

  const topOfPage = useRef<HTMLHeadingElement>(null);

  const offerAvailable = () => {
    if (!currentOffer) return false;
    return (
      Object.keys(currentOffer).length > 0 &&
      currentOffer?.Amount?.toString() === amountOptions[fundedAmountIndex]
    );
  };

  const updateOffer = async () => {
    const abortController = new AbortController();
    const response = await getBCAOffers(
      abortController,
      amountOptions[fundedAmountIndex],
      periodOptions[paymentPeriodIndex],
    );

    if (!response.ok) {
      toastMessageServiceSingleton.addError(response?.message, null, '', false, null, true);
      setOfferLoading(false);
      return;
    }
    const result = response.result;
    if (result?.offerDetails?.offer) {
      setCurrentOffer(result.offerDetails.offer);
      setSelectedOffer(result.offerDetails.offer);
      setIsBCATopUp(
        result.offerDetails.QualificationStatus === 1 && result.offerDetails.CurrentBalance > 0,
      );
    }
    setOfferLoading(false);
  };

  const submitAndAcceptOffer = async () => {
    const timeoutManager = controlledTimeout(() => {
      setBcaSubmitStatus(BCASubmitStatus.TAKING_LONG);
    }, 10000);
    setOfferAcceptLoading(true);
    setShowContractModal(false);
    setBcaSubmitStatus(BCASubmitStatus.COLLECTING_INFO);

    if (!currentOffer || !additionalFieldValues) {
      setOfferAcceptLoading(false);
      return;
    }

    const abortController = new AbortController();
    const submitResult = await submitBCAOffer(
      abortController,
      Number(amountOptions[fundedAmountIndex]),
      currentOffer.OfferId,
      additionalFieldValues,
    );

    if (!submitResult.ok) {
      setOfferAcceptLoading(false);
      setShowContractModal(false);
      toastMessageServiceSingleton.addError(
        submitResult.result?.Message ?? submitResult.message,
        null,
        '',
        false,
        null,
        true,
      );
      Sentry.captureException(
        `BCA Top Up: Submit offer call failed: ${merchant.merchantId}: ${JSON.stringify(
          submitResult,
        )}`,
      );
      Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, 'BCA');
      return;
    }

    const result = submitResult.result;
    const address = ['line1', 'line2', 'suburb', 'city', 'province', 'country', 'postcode']
      .map((addressItem) => result.address?.[addressItem])
      .filter((addressValue) => addressValue)
      .join(', ');

    const merchantOfferDetails: IMerchantDetails = {
      merchantName: result.merchantName,
      nameOfOwner: `${additionalFieldValues?.name} ${additionalFieldValues?.surname}`,
      idNumber: additionalFieldValues?.id,
      advanceStart: DateTime.fromJSDate(new Date()).toFormat('dd/LL/yyyy'),
      address: address,
      vatNumber: result.vatRegistrationNumber,
      advanceAmount: amountOptions[fundedAmountIndex],
      totalRepayment: currentOffer?.TotalRepayment,
      repaymentRate: currentOffer?.Rate,
    };

    setMerchantDetails(merchantOfferDetails);
    setBcaSubmitStatus(BCASubmitStatus.PREPARING_APPLICATION);
    timeoutManager.restart();
    await Delay(2000);

    const contractBase64 = await getContractPdfBase64(
      merchantOfferDetails,
      merchant,
      selectedOffer,
    );

    const params = {
      amount: amountOptions[fundedAmountIndex],
      applicationId: result.ApplicationId,
      accountHolderName: result.accountHolderName,
      branchCode: result.branchCode,
      fileBase64: contractBase64,
      bankName: result.bankName,
      accountNumber: result.accountNumber,
      merchantName: result.merchantName,
      merchantEmailId: result.merchantEmailId,
      bankAccountType: result.bankAccountType,
    };

    setBcaSubmitStatus(BCASubmitStatus.SUBMITTING_APPLICATION);
    timeoutManager.restart();
    await Delay(1000);

    const resultAccept = await acceptBCAOffer(params, true);

    if (!resultAccept.ok) {
      toastMessageServiceSingleton.addError(resultAccept?.message, null, '', false, null, true);
      setOfferAcceptLoading(false);
      Sentry.captureException(
        `BCA Top Up: accept terms call failed: ${merchant.merchantId}: ${JSON.stringify(
          resultAccept,
        )}`,
      );
      Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, 'BCA');
      return;
    }

    setChosenOffer(currentOffer ?? {});
    setShowContractModal(false);
    setShowApprovedModal(true);

    setOfferAcceptLoading(false);
    setBcaSubmitStatus(BCASubmitStatus.SUCCESS);
    timeoutManager.clear();
  };

  const updatePeriodSlider = (currentPeriod, amount = amountOptions[fundedAmountIndex]) => {
    if (!offers) return;
    let terms: number[] = offers?.amountTermsMap[amount]?.sort((a, b) => a - b) || [];
    setPeriodOptions(terms);

    let index = terms.findIndex((term) => currentPeriod === term);
    setPaymentPeriodIndex(index !== -1 ? index : 0);
  };

  useEffect(() => {
    if (topOfPage.current) {
      topOfPage.current.scrollIntoView(false);
    }
  }, []);

  useEffect(() => {
    const getBCAOfferOptions = async () => {
      if (!!offers && Object.keys(offers).length > 0) {
        let amounts = Object.keys(offers.amountTermsMap).sort((a, b) => Number(a) - Number(b));
        setAmountOptions(amounts);
        let amountIndex = amounts.findIndex((amount) =>
          selectedOffer !== currentOffer
            ? selectedOffer.Amount === parseInt(amount)
            : offers?.offerDetails?.offer?.Amount === parseInt(amount),
        );
        amountIndex = amountIndex !== -1 ? amountIndex : 0;
        updatePeriodSlider(offers.offerDetails?.offer?.Term, amounts[amountIndex]);

        setFundedAmountIndex(amountIndex);
        setCurrentOffer(offers.offerDetails.offer);
        setSelectedOffer(offers.offerDetails.offer);
      } else {
        return <Redirect push to={businessCashAdvanceRoute} />;
      }
    };
    getBCAOfferOptions();
  }, [offers]);

  useEffect(() => {
    if (amountOptions.length > 0) {
      updateOffer();
    }
  }, [debouncedFundedIndex, debouncedPaymentPeriod]);

  useEffect(() => {
    toastMessageServiceSingleton.removeAllToasts();
    BCATopUpTealiumService.onBCACalculationStart();
  }, []);

  useEffect(() => {
    const settlementAmount = async () => {
      setIsLoading(true);
      try {
        const abortController = new AbortController();
        const result = await getSettlementAmount(abortController, merchant.merchantId || '');

        setEarlySettlementAmount(result);
      } catch (error) {
        throw new Error((error as Error).message);
      } finally {
        setIsLoading(false);
      }
    };

    merchant.merchantId && settlementAmount();
  }, [merchant]);

  const tooltipContent = () => (
    <>
      <span className="is-icon-6 icon-cross is-pulled-right"></span>
      <div className="tooltip-text">
        <p>
          The percentage selected will be what we deduct from your daily turnover to repay your
          advance. This percentage deduction will remain the same until your advance is fully paid
          off. This is automatically completed before your daily settlement into your business
          account.
        </p>
        <br />
        <p>Remember, when you don't transact, you don't pay.</p>
      </div>
    </>
  );

  // TODO: BCA Flag to be removed
  if (Environment.current?.disabledFeatures.bcaTopUp) {
    return <Redirect push to={paymentsDashboardRoute} />;
  }

  if (redirect) {
    return <Redirect push to={redirect} />;
  }

  return (
    <article className="BCACalculation [&_.NavigationFooter]:sticky [&_.NavigationFooter_.button]:py-3  [&_.NavigationFooter_.button]:px-16 [&_.NavigationFooter_.button]:!w-fit">
      <HeaderNav
        title={merchant?.companyName ? `Hi, welcome back ${merchant.companyName}` : ''}
        screenName="Business Cash Advance Top Up"
      >
        <FullPageLoader showLoader={isLoading} />
        <div className="px-4 mb-20 md:px-8">
          <AdditionalFieldsModal
            show={showAdditionalFieldsModal}
            isBCATopUp={true}
            onHide={() => setShowAdditionalFieldsModal(false)}
            onNext={(values: AdditionalFieldsValues) => {
              setAdditionalFieldValues(values);
              setShowAdditionalFieldsModal(false);
              BCATopUpTealiumService.onUIInteraction(
                BCAPageName.BCA_TOP_UP,
                BCALinkID.NEXT,
                BCAPageUrl.BCA_TOP_UP,
              );
              history.push(businessCashAdvanceTopUpContractRoute);
              setHideNextButton(false);
            }}
          />
          <AcceptContractModal
            show={showContractModal}
            onHide={() => setShowContractModal(false)}
            onAcceptOffer={() => {
              submitAndAcceptOffer();
            }}
            currentOffer={currentOffer}
            disabled={offerAcceptLoading}
          />
          <ApprovedModal
            show={showApprovedModal}
            onHide={() => {
              setShowApprovedModal(false);
              setRedirect(businessCashAdvanceDashboardRoute);
            }}
            onApplicationSuccessful={() => {
              const amount = currentOffer?.Amount || -1;
              const total = currentOffer?.TotalRepayment || -1;
              const term = currentOffer?.Term || -1;
              const id = currentOffer?.OfferId || 'unknown';

              TealiumEvents.bcaSuccessful(
                amount.toString(),
                total.toString(),
                term.toString(),
                id.substring(0, 19),
              );
            }}
            onRedirectContract={() => setRedirect(businessCashAdvanceDigitalTermsRoute)}
          />
          <SubmitModal status={bcaSubmitStatus} show={offerAcceptLoading} />
          <h1
            className="text-xl font-medium text-center md:text-4xl md:text-left"
            automationid="Business_Cash_Advance_Calculation_H1_Page_Heading"
            ref={topOfPage}
          >
            You qualify for a Business Cash Advance Top-Up with a maximum limit of R{' '}
            {formatCurrencyAmountSimple(offers?.offerDetails.AvailableLimit)}!
          </h1>

          <section className="my-8 text-base">
            <p className="font-medium text-center md:text-left">How it works:</p>
            <ul className="pl-3 text-center list-disc list-inside md:text-left">
              <li>To accept the Top-Up, you simply select your desired amount and proceed.</li>
              <li>
                Your outstanding balance will first be paid off, and then you will receive the
                remaining amount.
              </li>
              <li>
                The top-up amount will be in addition to your existing BCA funding, and all costs
                associated with it will apply.
              </li>
              <li>
                Your daily repayments are calculated as a percentage (repayment factor rate) of your
                daily earnings.
              </li>
            </ul>
          </section>

          <section className="flex flex-col gap-8">
            <div className="flex flex-col gap-8 px-6 pt-4 bg-grey-900 rounded-4">
              <h2 className="text-lg font-medium md:text-base text-grey-200">
                Funding information
              </h2>
              <div className="relative flex flex-col gap-5 p-5 bg-grey-800 rounded-16">
                <h3 className="text-sm font-medium md:text-base text-grey-300">Funding amount</h3>
                <div className="columns is-mobile">
                  <div className="column is-narrow">
                    <div
                      className="slider-down is-clickable"
                      onClick={() => {
                        BCATopUpTealiumService.onOfferSliderChange();
                        if (fundedAmountIndex > 0) {
                          const newFundedAmountIndex = fundedAmountIndex - 1;
                          setOfferLoading(true);
                          setFundedAmountIndex(newFundedAmountIndex);
                          updatePeriodSlider(
                            periodOptions[paymentPeriodIndex],
                            amountOptions[newFundedAmountIndex],
                          );
                        }
                      }}
                    >
                      <span className="is-icon-5 icon-minus"></span>
                    </div>
                  </div>
                  <div className="column">
                    <Slider
                      disabled={amountOptions.length <= 1}
                      xMax={amountOptions.length - 1 || 0}
                      x={fundedAmountIndex}
                      isMobile={false}
                      onChange={({ x }) => {
                        BCATopUpTealiumService.onOfferSliderChange();
                        setOfferLoading(true);
                        setFundedAmountIndex(x);
                        updatePeriodSlider(periodOptions[paymentPeriodIndex], amountOptions[x]);
                      }}
                      xLabelFormatter={(x) =>
                        `R ${
                          amountOptions[x]?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') || ''
                        }`
                      }
                      automationIdPrefix="Business_Cash_Advance_Calculation_Slider_How_Much_Do_You_Need"
                    />
                  </div>
                  <div className="column is-narrow">
                    <div
                      className="slider-down is-clickable"
                      onClick={() => {
                        BCATopUpTealiumService.onOfferSliderChange();
                        if (fundedAmountIndex < amountOptions.length - 1) {
                          const newFundedAmountIndex = fundedAmountIndex + 1;
                          setOfferLoading(true);
                          setFundedAmountIndex(newFundedAmountIndex);
                          updatePeriodSlider(
                            periodOptions[paymentPeriodIndex],
                            amountOptions[newFundedAmountIndex],
                          );
                        }
                      }}
                    >
                      <span className="is-icon-5 icon-plus"></span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative flex flex-col gap-5 p-5 rounded-16 bg-grey-800">
                <h3 className="text-sm font-medium md:text-base text-grey-300">
                  Approximate repayment term
                </h3>
                <div className="columns is-mobile">
                  <div className="column is-narrow">
                    <div
                      className="slider-down is-clickable"
                      onClick={() => {
                        BCATopUpTealiumService.onRepaymentSliderChange();
                        if (paymentPeriodIndex > 0) {
                          setOfferLoading(true);
                          setPaymentPeriodIndex(paymentPeriodIndex - 1);
                        }
                      }}
                    >
                      <span className="is-icon-5 icon-minus"></span>
                    </div>
                  </div>
                  <div className="column">
                    <Slider
                      disabled={periodOptions.length <= 1}
                      xMax={periodOptions.length - 1 || 0}
                      x={paymentPeriodIndex || 0}
                      isMobile={false}
                      onChange={({ x }) => {
                        BCATopUpTealiumService.onRepaymentSliderChange();
                        setOfferLoading(true);
                        setPaymentPeriodIndex(x);
                      }}
                      xLabelFormatter={(x) =>
                        periodOptions[x] && periodOptions.length > 1
                          ? `${periodOptions[x]} months`
                          : ''
                      }
                      automationIdPrefix="Business_Cash_Advance_Calculation_Slider_Estimated_Payment_Period"
                      singleOption={(x) =>
                        periodOptions.length > 1 ? '' : `${periodOptions[x ?? ''] || '  '} months`
                      }
                    />
                  </div>
                  <div className="column is-narrow">
                    <div
                      className="slider-down is-clickable"
                      onClick={() => {
                        BCATopUpTealiumService.onRepaymentSliderChange();
                        if (paymentPeriodIndex < periodOptions.length - 1) {
                          setOfferLoading(true);
                          setPaymentPeriodIndex(paymentPeriodIndex + 1);
                        }
                      }}
                    >
                      <span className="is-icon-5 icon-plus"></span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between p-5 mx-6 text-sm font-medium md:text-base rounded-16 bg-grey-800">
              <div className="flex items-center">
                <h3>Repayment rate &nbsp;</h3>
                <Tooltip
                  showOnHover
                  isMobile={false}
                  onOpenEvent={() => {
                    BCATopUpTealiumService.onUIInteraction(
                      BCAPageName.BCA_TOP_UP,
                      BCALinkID.PAYMENT_RATE_TOOLTIP,
                      BCAPageUrl.BCA_TOP_UP,
                    );
                  }}
                  lightMode
                  content={tooltipContent()}
                >
                  <img className="w-5 h-5 cursor-pointer" src={InfoImage} />
                </Tooltip>
              </div>

              <div className="text-sm font-bold text-white md:text-xl">
                {!offerLoading ? (
                  offerAvailable() ? (
                    `${currentOffer?.Rate}%`
                  ) : (
                    'No data'
                  )
                ) : (
                  <StaticLoader />
                )}
              </div>
            </div>

            <div className="flex items-center justify-between p-5 mx-6 text-sm font-medium md:text-base rounded-16 bg-grey-800">
              <p>Current early settlement amount</p>
              <p className="text-sm font-bold md:text-xl">
                R {formatCurrencyAmountSimple(earlySettlementAmount)}
              </p>
            </div>

            <div className="flex items-center justify-between gap-3 p-5 mx-6 text-sm font-medium md:text-base rounded-16 bg-grey-500">
              <p className="break-words">Amount disbursed after settlement</p>
              <p className="text-sm font-bold md:text-xl w-fit">
                R{' '}
                {currentOffer?.Amount &&
                  formatCurrencyAmountSimple(Math.abs(currentOffer.Amount - earlySettlementAmount))}
              </p>
            </div>
          </section>
        </div>
      </HeaderNav>

      {!hideNextButton && (
        <NavigationFooter
          onBack={() => false}
          showBackButton={false}
          onNext={() => {
            BCATopUpTealiumService.onUIInteraction(
              BCAPageName.BCA_TOP_UP,
              BCALinkID.SAVE_AND_CONTINUE,
              BCAPageUrl.BCA_TOP_UP,
            );
            setHideNextButton(isMobile);
            setShowAdditionalFieldsModal(true);
          }}
          nextEnabled={!offerLoading || offerAvailable()}
          backEnabled={false}
          backLabel="Back"
          nextLabel={'Save & proceed'}
        />
      )}
    </article>
  );
}
